/**
 * PATH: packages/drafting/src/DraftingStudio.tsx
 * FILE_ID: DRAFTING-STUDIO-001
 * PURPOSE: The Drafting Studio shell — composes the toolbar, the
 *   properties/layers/export rail, and the Konva canvas around one
 *   useDrafting state. This is the single component consuming apps mount.
 * WHY: A self-contained, transport-agnostic studio (no app coupling in
 *   P1) so it can drop into Email Hub now and any hub later (scope §7).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type Konva from 'konva'
import { useDrafting } from './hooks/useDrafting'
import { useAiActions } from './hooks/useAiActions'
import type { DrawingIR } from './ir/schema'
import type { DraftingAiHandlers } from './ai/types'
import { DraftCanvas } from './canvas/DraftCanvas'
import { ToolPanel } from './panels/ToolPanel'
import { InputPanel } from './panels/InputPanel'
import { GeneratePanel } from './panels/GeneratePanel'
import { LayerPanel } from './panels/LayerPanel'
import { ExportPanel } from './panels/ExportPanel'
import {
  downloadPng,
  downloadSvg,
  downloadJson,
  stagePngDataUrl,
  dataUrlToFile,
  drawingFilename,
} from './export/exporters'

export interface DraftingStudioProps extends DraftingAiHandlers {
  /** Seed the studio with an existing drawing (else a blank default). */
  initialDrawing?: DrawingIR
  /** Notified on every IR change — wire to persistence in P2. */
  onChange?: (ir: DrawingIR) => void
  /** "Insert into email" — receives a 2× PNG data URL of the live canvas.
   *  When omitted the button is hidden (keeps the studio transport-agnostic
   *  so non-email hosts don't show an email action). */
  onInsertImage?: (dataUrl: string) => void
  /** "Send as attachment" — receives a PNG File of the live canvas. */
  onAttach?: (file: File) => void
  className?: string
}

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

export function DraftingStudio(props: DraftingStudioProps) {
  const {
    initialDrawing, onChange, onInsertImage, onAttach, className,
    onGenerate, onRefine, onUploadSketch,
  } = props
  const d = useDrafting(initialDrawing)
  const stageRef = useRef<Konva.Stage | null>(null)
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null)

  const ai = useAiActions({
    scale: d.ir.meta.scale.ratio,
    units: d.ir.meta.units,
    currentIr: d.ir,
    replaceIr: d.replaceIr,
    onGenerate,
    onRefine,
    onUploadSketch,
  })
  const aiEnabled = !!onGenerate

  useEffect(() => {
    onChange?.(d.ir)
  }, [d.ir, onChange])

  const onDeleteSelected = useCallback(() => {
    if (d.selectedId) d.deleteEntity(d.selectedId)
  }, [d])

  // Email hand-off — both paths raster the SAME live stage via stagePngDataUrl
  // so what the user sees is exactly what lands in the email. Wired only when
  // the host supplies the callbacks (apps/email/DraftingPage).
  const handleInsertImage = useCallback(() => {
    const url = stagePngDataUrl(stageRef.current)
    if (url) onInsertImage?.(url)
  }, [onInsertImage])

  const handleAttach = useCallback(() => {
    const url = stagePngDataUrl(stageRef.current)
    if (!url) return
    onAttach?.(dataUrlToFile(url, drawingFilename(d.ir, 'png')))
  }, [onAttach, d.ir])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return
      if ((e.key === 'Delete' || e.key === 'Backspace') && d.selectedId) {
        e.preventDefault()
        d.deleteEntity(d.selectedId)
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) d.redo()
        else d.undo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [d])

  return (
    <div className={`flex h-full min-h-0 w-full flex-col ${className ?? ''}`}>
      <ToolPanel
        tool={d.tool}
        hasSelection={!!d.selectedId}
        canUndo={d.canUndo}
        canRedo={d.canRedo}
        onTool={d.setTool}
        onUndo={d.undo}
        onRedo={d.redo}
        onDelete={onDeleteSelected}
      />
      <div className="flex min-h-0 flex-1">
        <aside className="w-64 shrink-0 space-y-4 overflow-y-auto border-r border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
          {aiEnabled && (
            <GeneratePanel
              scale={d.ir.meta.scale.ratio}
              units={d.ir.meta.units}
              generating={ai.generating}
              status={ai.status}
              error={ai.error}
              canRefine={!!onRefine && d.ir.entities.length > 0}
              onGenerate={(prompt, sketch) => void ai.generate(prompt, sketch)}
              onRefine={(instruction) => void ai.refine(instruction)}
              onBackground={setBackgroundUrl}
              hasBackground={!!backgroundUrl}
            />
          )}
          <InputPanel meta={d.ir.meta} onMeta={d.updateMeta} />
          <LayerPanel
            layers={d.ir.layers}
            activeLayerId={d.activeLayerId}
            onActivate={d.setActiveLayerId}
            onToggleVisible={d.toggleLayerVisible}
          />
          <ExportPanel
            report={d.report}
            onPng={() => downloadPng(stageRef.current, d.ir)}
            onSvg={() => downloadSvg(d.ir)}
            onJson={() => downloadJson(d.ir)}
            onInsertImage={onInsertImage ? handleInsertImage : undefined}
            onAttach={onAttach ? handleAttach : undefined}
          />
        </aside>
        <main className="min-w-0 flex-1">
          <DraftCanvas
            ir={d.ir}
            tool={d.tool}
            activeLayerId={d.activeLayerId}
            selectedId={d.selectedId}
            backgroundUrl={backgroundUrl}
            onSelect={d.setSelectedId}
            onAddEntity={d.addEntity}
            onUpdateEntity={d.updateEntity}
            stageRef={stageRef}
          />
        </main>
      </div>
    </div>
  )
}
